import React from 'react';

import BackIcon from '@mui/icons-material/ArrowBack';
import ForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { photoItemListSelector, childFacilitySelector } from '../../selector';
import {
  execPurchasePhotos,
  execPurchasePhotosByCreditCard,
  refreshPhotoItemList,
} from '../../thunk';

import Purchase from './Purchase';
import SelectPhoto from './SelectPhoto';
import SelectPhotoType from './SelectPhotoType';
import { GroupDetailController } from './groupDetailController';

import type { PurchasePhotoType } from './SelectPhotoType';
import type { EntityDataPubNotNullType } from '@/domain/entityPub';
import type { PhotoSalesJsonPub, PhotoSalesJsonPvt } from '@/types/domain/photo/entities/photoSalesEntity';

import DialogTitle from '@/components/DialogTitle';
import DialogTransition from '@/components/DialogTransition';
import MessageBox from '@/components/MessageBox';
import PaymentDialog from '@/domain/payment/component/PaymentDialog';
import { uiSlice } from '@/domain/ui/redux';

/**
 * プロパティ
 */
export type Props = {
  /** 閉じる */
  close: () => void,
  /** 販売 */
  sales: EntityDataPubNotNullType<PhotoSalesJsonPvt, PhotoSalesJsonPub>,
};

type PhotoType = {
  id: string,
  name: string,
}

const GroupDetail: React.FC<Props> = ({ close, sales }) => {
  const dispatch = useDispatch();
  const photoItemList = useSelector(photoItemListSelector);
  const [isOpenPayment, setIsOpenPayment] = React.useState(false);
  const [isOpenCloseConfirm, setIsOpenCloseConfirm] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [purchasePhotos, setPurchasePhotos] = React.useState<PurchasePhotoType[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const nurseryId = useSelector(childFacilitySelector);
  React.useEffect(() => {
    if (photoItemList === undefined) {
      dispatch(refreshPhotoItemList({ nurseryId }));
    }
  }, [dispatch, nurseryId, photoItemList]);
  const photoTypeList = photoItemList || [];
  const steps = ['写真選択', '写真種別', '確認・購入'];
  const controller = new GroupDetailController(purchasePhotos, photoTypeList);
  const itemData: PhotoType[] = sales.data.photos.map((v) => ({ id: v.photoId }))
    .sort((a, b) => (a.id > b.id ? 1 : -1))
    .map((v, index) => ({ id: v.id, name: `No.${index + 1}` }));
  let comp = null;
  let compAction = null;
  const { totalPrice, totalNumItems } = controller.getTotalPrices();
  if (activeStep === 0) {
    comp = (
      <SelectPhoto
        photoList={itemData}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        nurseryId={nurseryId}
      />
    );
    const handleCompSelectPhoto = () => {
      const selectedPhotos = [];
      for (let i = 0; i < itemData.length; i += 1) {
        const item = itemData[i];
        if (selectedIds.includes(item.id)) {
          selectedPhotos.push({
            ...item,
            photoTypes: [{
              type: { ...photoTypeList[0] },
              num: 1,
            }],
          });
        }
      }
      setPurchasePhotos(selectedPhotos);
      setActiveStep(1);
    };
    compAction = (
      <Button
        endIcon={<ForwardIcon />}
        variant="contained"
        color="primary"
        onClick={handleCompSelectPhoto}
        disabled={selectedIds.length === 0}
      >
        写真種別選択へ
      </Button>
    );
  } else if (activeStep === 1) {
    comp = (
      <SelectPhotoType
        nurseryId={nurseryId}
        purchasePhotoList={purchasePhotos}
        photoTypeList={photoTypeList}
        selectPhotoTypeAll={(isAdd, photoType) => {
          setPurchasePhotos(controller.addOrRemovePhotoTypeAll(isAdd, photoType));
        }}
        setPhotoType={(isAdd, photoType, photoId, num) => {
          setPurchasePhotos(controller.addOrRemovePhotoType(
            isAdd,
            { type: photoType, num },
            photoId,
          ));
        }}
        setPhotoTypeNum={(photoType, photoId, num) => {
          setPurchasePhotos(controller.setPhotoNum(
            { type: photoType, num },
            photoId,
          ));
        }}
      />
    );
    const handleCompSelectPhotoType = () => {
      if (totalNumItems > 300) {
        dispatch(uiSlice.actions.setMessage({
          key: 'error',
          content: '1度に購入できる写真の種類は300種類までです',
        }));
      } else {
        setActiveStep(2);
      }
    };
    compAction = (
      <>
        <Button startIcon={<BackIcon />} variant="contained" color="primary" onClick={() => setActiveStep(0)}>
          写真選択へ戻る
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          endIcon={<ForwardIcon />}
          variant="contained"
          color="primary"
          onClick={handleCompSelectPhotoType}
          disabled={!controller.checkIsAllPurchasePhotoTypeSelected()}
        >
          確認・購入へ
        </Button>
      </>
    );
  } else {
    comp = (
      <Purchase
        photoList={purchasePhotos}
        controller={controller}
        nurseryId={nurseryId}
      />
    );
    compAction = (
      <>
        <Button startIcon={<BackIcon />} variant="contained" color="primary" onClick={() => setActiveStep(1)}>
          種別選択へ戻る
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          endIcon={<ForwardIcon />}
          variant="contained"
          color="primary"
          onClick={() => setIsOpenPayment(true)}
        >
          支払い手続き
        </Button>
      </>
    );
  }
  return (
    <Dialog
      open
      onClose={() => close()}
      fullScreen
      TransitionComponent={DialogTransition}
    >
      <DialogTitle title={`写真販売「${sales.data.salesGroupName}」`} />
      <DialogContent>
        <Stepper nonLinear activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {comp}
        {isOpenPayment && (
          <PaymentDialog
            amount={totalPrice}
            payByCash={(amount) => {
              const photos: { photoId: string, photoItemId: string, numberOfItems: number }[] = [];
              for (let i = 0; i < purchasePhotos.length; i += 1) {
                for (let j = 0; j < purchasePhotos[i].photoTypes.length; j += 1) {
                  photos.push({
                    photoId: purchasePhotos[i].id,
                    photoItemId: purchasePhotos[i].photoTypes[j].type.photoItemId,
                    numberOfItems: purchasePhotos[i].photoTypes[j].num,
                  });
                }
              }
              dispatch(execPurchasePhotos({
                amount,
                nurseryId,
                groupId: sales.data.salesGroupId,
                photos,
              }));
              setIsOpenPayment(false);
              close();
            }}
            payByCreditCard={(amount, cardId) => {
              const photos: { photoId: string, photoItemId: string, numberOfItems: number }[] = [];
              for (let i = 0; i < purchasePhotos.length; i += 1) {
                for (let j = 0; j < purchasePhotos[i].photoTypes.length; j += 1) {
                  photos.push({
                    photoId: purchasePhotos[i].id,
                    photoItemId: purchasePhotos[i].photoTypes[j].type.photoItemId,
                    numberOfItems: purchasePhotos[i].photoTypes[j].num,
                  });
                }
              }
              dispatch(execPurchasePhotosByCreditCard({
                amount,
                cardId,
                nurseryId,
                groupId: sales.data.salesGroupId,
                photos,
              }));
              setIsOpenPayment(false);
              close();
            }}
            close={() => setIsOpenPayment(false)}
            nurseryId={nurseryId}
          />
        )}
      </DialogContent>
      <DialogActions>
        {compAction}
        <Button color="primary" onClick={() => setIsOpenCloseConfirm(true)}>
          閉じる
        </Button>
        {isOpenCloseConfirm && (
          <MessageBox
            title="写真選択がクリアされます"
            content="閉じると写真選択がクリアされますが、よろしいでしょうか？"
            close={(isYes) => {
              if (isYes) {
                close();
              }
              setIsOpenCloseConfirm(false);
            }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GroupDetail;
